"use client";

import { useState } from "react";
import { MessageSquareText, Bug } from "lucide-react";
import { FeedbackDialog } from "@/components/ui/support/feedback";
import { BugReportDialog } from "@/components/ui/support/bugReport";
import { Button } from "@/components/ui/shadcn/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/shadcn/popover";
import { TwoStageTooltip } from "@/components/ui/common/TwoStageTooltip";

type DialogView = "closed" | "feedback" | "bug";

// Feedback button for navbar (all sizes)
export function MobileFeedbackButton() {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogView, setDialogView] = useState<DialogView>("closed");

  function openDialog(view: "feedback" | "bug") {
    setPopoverOpen(false);
    setDialogView(view);
  }

  function closeDialog() {
    setDialogView("closed");
  }

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <TwoStageTooltip
          label="Feedback"
          hint="Report bugs or share ideas"
          side="bottom"
          disabled={popoverOpen}
        >
          <PopoverTrigger asChild>
            <Button
              aria-label="Open feedback menu"
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-md text-muted-foreground transition hover:bg-accent hover:text-foreground focus:outline-none"
            >
              <MessageSquareText className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
        </TwoStageTooltip>

        <PopoverContent side="bottom" align="end" sideOffset={8} className="w-64 p-2">
          <div className="space-y-1">
            <button
              onClick={() => openDialog("feedback")}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition hover:bg-accent"
            >
              <div className="rounded-md bg-yellow-500/20 p-1.5">
                <MessageSquareText className="h-4 w-4 text-yellow-400" />
              </div>
              <div>
                <div className="font-medium text-foreground">Feedback</div>
                <div className="text-xs text-muted-foreground">Share what could be better</div>
              </div>
            </button>

            <button
              onClick={() => openDialog("bug")}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition hover:bg-accent"
            >
              <div className="rounded-md bg-orange-500/20 p-1.5">
                <Bug className="h-4 w-4 text-orange-400" />
              </div>
              <div>
                <div className="font-medium text-foreground">Report a bug</div>
                <div className="text-xs text-muted-foreground">Something not working?</div>
              </div>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <FeedbackDialog
        open={dialogView === "feedback"}
        onClose={closeDialog}
        onBack={closeDialog}
        triggeredBy="mobile:feedback"
      />

      <BugReportDialog
        open={dialogView === "bug"}
        onClose={closeDialog}
        onBack={closeDialog}
        source="mobile"
        enableAttachments={true}
      />
    </>
  );
}
